import type {
  ApplicableRiskReductionMeasureRow,
  RiskReductionMeasure,
  RiskReductionMeasureType,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import {
  groupRiskReductionMeasuresByType,
  RRM_HIERARCHY_TYPE_ORDER,
} from './rrmHierarchyTypes'

export type MeasurePickerMode = 'full' | 'applicable' | 'applicable-empty'

export type ApplicableMeasureReasonMap = Map<string, string>

export function buildApplicableReasonMap(
  rows: ApplicableRiskReductionMeasureRow[]
): ApplicableMeasureReasonMap {
  return new Map(
    rows.map((row) => [row.riskReductionMeasureId, row.reason.trim()])
  )
}

export function resolveMeasurePickerMode(input: {
  hasSuggestionResult: boolean
  showSuggestedOnly: boolean
  applicableIds: string[]
}): MeasurePickerMode {
  if (!input.hasSuggestionResult || !input.showSuggestedOnly) {
    return 'full'
  }
  if (input.applicableIds.length === 0) {
    return 'applicable-empty'
  }
  return 'applicable'
}

export function filterMeasuresForPickerMode(
  allMeasures: RiskReductionMeasure[],
  mode: MeasurePickerMode,
  applicableIds: Set<string>
): RiskReductionMeasure[] {
  if (mode === 'full') {
    return allMeasures
  }
  if (mode === 'applicable-empty') {
    return []
  }
  return allMeasures.filter((measure) => applicableIds.has(measure.id))
}

export function sortMeasuresSuggestedFirst<
  T extends { id: string },
>(measures: T[], suggestedIds: Set<string>): T[] {
  if (suggestedIds.size === 0) {
    return measures
  }

  return [...measures].sort((left, right) => {
    const leftSuggested = suggestedIds.has(left.id)
    const rightSuggested = suggestedIds.has(right.id)
    if (leftSuggested === rightSuggested) {
      return 0
    }
    if (leftSuggested) {
      return -1
    }
    return 1
  })
}

export function groupMeasuresForPicker(
  measures: RiskReductionMeasure[],
  suggestedIds?: Set<string>
): Map<RiskReductionMeasureType, RiskReductionMeasure[]> {
  const grouped = groupRiskReductionMeasuresByType(measures)
  if (suggestedIds == null || suggestedIds.size === 0) {
    return grouped
  }

  for (const type of RRM_HIERARCHY_TYPE_ORDER) {
    const list = grouped.get(type) ?? []
    grouped.set(type, sortMeasuresSuggestedFirst(list, suggestedIds))
  }

  return grouped
}

export { RRM_HIERARCHY_TYPE_ORDER }
